import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

export default function Home() {
  // 从 localStorage 恢复搜索状态
  const savedSearchState = typeof window !== 'undefined' ? localStorage.getItem('goSearchState') : null;
  const initialState = savedSearchState ? JSON.parse(savedSearchState) : null;
  
  // 棋手搜索状态
  const [player1, setPlayer1] = useState(initialState?.player1 || '');
  const [player2, setPlayer2] = useState(initialState?.player2 || '');
  
  // 比赛搜索状态
  const [tournament, setTournament] = useState(initialState?.tournament || '');
  
  // 搜索结果状态
  const [games, setGames] = useState(initialState?.games || []);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(initialState?.searched || false);
  const [totalCount, setTotalCount] = useState(initialState?.totalCount || 0);
  const [currentPage, setCurrentPage] = useState(initialState?.currentPage || 1);
  const [totalPages, setTotalPages] = useState(initialState?.totalPages || 0);
  
  // 输入框的页码值（用于实时跟踪用户输入）
  const [pageInputValue, setPageInputValue] = useState(initialState?.pageInputValue || "1");
  
  // 搜索类型：'player' 或 'tournament'
  const [searchType, setSearchType] = useState(initialState?.searchType || 'player');
  
  // 响应式每页显示数量
  const getPageSize = () => {
    // 检测屏幕宽度
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width < 640) { // 手机端
        return 5;
      } else if (width < 1024) { // 平板端
        return 5;
      } else { // PC端
        return 20;
      }
    }
    return 20; // 默认值
  };
  
  const [pageSize, setPageSize] = useState(20);
  
  // 用户自定义每页显示数量 - 初始值根据屏幕大小设置
  const [customPageSize, setCustomPageSize] = useState(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width < 640) return 5;   // 手机端
      if (width < 1024) return 5; // 平板端
    }
    return 20; // PC端或默认
  });
  
  // 是否已应用自定义每页显示数量
  const [isCustomSizeApplied, setIsCustomSizeApplied] = useState(false);
  
  // 每页显示数量选项（增加5条选项适应手机端）
  const pageSizeOptions = [5, 10, 15, 20, 30, 50, 100];
  
  // 从 localStorage 恢复搜索状态 - 只在组件挂载时执行一次
  useEffect(() => {
    if (initialState && initialState.searched) {
      // 状态已通过 useState 初始值恢复，这里不需要做任何事
    }
  }, []);
  
  // 保存搜索状态到 localStorage
  useEffect(() => {
    if (searched) {
      const searchState = {
        player1,
        player2,
        tournament,
        games,
        searched,
        totalCount,
        currentPage,
        totalPages,
        pageInputValue,
        searchType
      };
      localStorage.setItem('goSearchState', JSON.stringify(searchState));
    }
  }, [player1, player2, tournament, games, searched, totalCount, currentPage, totalPages, pageInputValue, searchType]);

  // 从搜索结果中获取棋手实际姓名
  const getDisplayPlayerName = (searchTerm, gameList, isPlayer1 = true) => {
    if (!searchTerm || !gameList || gameList.length === 0) return searchTerm;
    
    // 如果是汉字，直接返回
    if (/[\u4e00-\u9fa5]/.test(searchTerm)) {
      return searchTerm;
    }
    
    // 如果是英文/拼音首字母，从搜索结果中找出最匹配的棋手名
    if (/^[a-zA-Z]+$/.test(searchTerm)) {
      // 当搜索两个棋手时，需要更精确的匹配
      if (player1 && player2 && gameList.length > 0) {
        // 对于双方搜索，我们需要找出两个不同的棋手
        // 首先收集所有棋手
        const allPlayers = new Set();
        for (const game of gameList) {
          if (game.black_player) allPlayers.add(game.black_player);
          if (game.white_player) allPlayers.add(game.white_player);
        }
        
        // 将棋手列表转换为数组
        const playerArray = Array.from(allPlayers);
        
        if (playerArray.length >= 2) {
          // 如果有两个或更多棋手，尝试根据拼音首字母匹配
          // 简单的拼音首字母提取函数（简化版）
          const getSimpleInitials = (name) => {
            // 常见棋手拼音首字母映射
            const pinyinMap = {
              '马晓春': 'mxc',
              '刘小光': 'lxg',
              '刘昌赫': 'lch',
              '李昌镐': 'lcg',
              '聂卫平': 'nwp',
              '古力': 'gl',
              '依田纪基': 'ytjj',
              '赵治勋': 'zzx',
              '曹薰铉': 'cxx',
              '林海峰': 'lhf',
              '加藤正夫': 'jtzf',
              '小林光一': 'xlgy',
              '王立诚': 'wlc',
              '张栩': 'zx',
              '陈嘉锐': 'cjr',
              '徐奉洙': 'xfs',
              '石田芳夫': 'stff',
              '高尾绅路': 'gwsl',
              '麦克雷蒙': 'mklm',
              '黄奕中': 'hyz',
              '大竹英雄': 'dzyx',
              '淡路修三': 'dlxs',
              '工藤纪夫': 'gtjf',
              '桥本昌二': 'qbc2',
              '柳时熏': 'lsx',
              '梁宰豪': 'lzh',
              '宋泰坤': 'stk',
              '宫泽吾朗': 'gzwl',
              '羽根直树': 'ygzs',
              '山下敬吾': 'xsjw',
              '武宫正树': 'wgzs',
              '宫秀宇太郎': 'gxytl'
            };
            
            return pinyinMap[name] || '';
          };
          
          // 尝试匹配搜索词（忽略大小写）
          for (const player of playerArray) {
            const playerInitials = getSimpleInitials(player);
            if (playerInitials.toLowerCase() === searchTerm.toLowerCase()) {
              return player;
            }
          }
          
          // 如果没有精确匹配，返回前两个棋手（按出现频率排序）
          const playerCounts = {};
          for (const game of gameList) {
            if (game.black_player) playerCounts[game.black_player] = (playerCounts[game.black_player] || 0) + 1;
            if (game.white_player) playerCounts[game.white_player] = (playerCounts[game.white_player] || 0) + 1;
          }
          
          const sortedPlayers = Object.entries(playerCounts)
            .sort((a, b) => b[1] - a[1])
            .map(entry => entry[0]);
          
          if (sortedPlayers.length >= 2) {
            return isPlayer1 ? sortedPlayers[0] : sortedPlayers[1];
          }
        }
        
        // 如果只有一个棋手或没有匹配，返回原搜索词
        return searchTerm;
      } else {
        // 单方搜索，使用原来的逻辑
        const playerCounts = {};
        
        for (const game of gameList) {
          if (game.black_player) {
            playerCounts[game.black_player] = (playerCounts[game.black_player] || 0) + 1;
          }
          if (game.white_player) {
            playerCounts[game.white_player] = (playerCounts[game.white_player] || 0) + 1;
          }
        }
        
        let mostFrequentPlayer = null;
        let maxCount = 0;
        
        for (const [player, count] of Object.entries(playerCounts)) {
          if (count > maxCount) {
            maxCount = count;
            mostFrequentPlayer = player;
          }
        }
        
        return mostFrequentPlayer || searchTerm;
      }
    }
    
    return searchTerm;
  };

  // 从搜索结果中获取比赛实际名称
  const getDisplayTournamentName = (searchTerm, gameList) => {
    if (!searchTerm || !gameList || gameList.length === 0) return searchTerm;
    
    // 如果是汉字，直接返回
    if (/[\u4e00-\u9fa5]/.test(searchTerm)) {
      return searchTerm;
    }
    
    // 如果是英文/拼音首字母，从搜索结果中找出最匹配的比赛名
    if (/^[a-zA-Z]+$/.test(searchTerm)) {
      // 统计所有比赛出现频率
      const tournamentCounts = {};
      
      for (const game of gameList) {
        if (game.tournament) {
          tournamentCounts[game.tournament] = (tournamentCounts[game.tournament] || 0) + 1;
        }
      }
      
      // 找出出现频率最高的比赛
      let mostFrequentTournament = null;
      let maxCount = 0;
      
      for (const [tournamentName, count] of Object.entries(tournamentCounts)) {
        if (count > maxCount) {
          maxCount = count;
          mostFrequentTournament = tournamentName;
        }
      }
      
      // 如果找到了，返回比赛名，否则返回原搜索词
      return mostFrequentTournament || searchTerm;
    }
    
    return searchTerm;
  };

  // 棋手搜索
  const handlePlayerSearch = async (page = 1) => {
    if (!player1.trim() && !player2.trim()) return;
    
    setSearchType('player');
    setLoading(true);
    setSearched(true);
    
    try {
      let searchUrl = '/api/games/search?';
      const params = [];
      
      if (player1.trim()) {
        params.push(`player1=${encodeURIComponent(player1.trim())}`);
      }
      if (player2.trim()) {
        params.push(`player2=${encodeURIComponent(player2.trim())}`);
      }
      
      // 使用当前生效的每页显示数量（如果已应用自定义，使用自定义值）
      const currentPageSize = isCustomSizeApplied ? customPageSize : getPageSize();
      params.push(`limit=${currentPageSize}`);
      params.push(`page=${page}`);
      
      searchUrl += params.join('&');
      
      const res = await axios.get(searchUrl);
      setGames(res.data.games);
      setTotalCount(res.data.total || res.data.count);
      setCurrentPage(page);
      setPageInputValue(page.toString()); // 同步输入框值
      setTotalPages(Math.ceil((res.data.total || res.data.count) / currentPageSize));
      setPageSize(currentPageSize);
    } catch (error) {
      console.error('棋手搜索失败:', error);
      alert('搜索失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 比赛搜索
  const handleTournamentSearch = async (page = 1) => {
    if (!tournament.trim()) return;
    
    setSearchType('tournament');
    setLoading(true);
    setSearched(true);
    
    try {
      // 使用当前生效的每页显示数量（如果已应用自定义，使用自定义值）
      const currentPageSize = isCustomSizeApplied ? customPageSize : getPageSize();
      const searchUrl = `/api/games/search?tournament=${encodeURIComponent(tournament.trim())}&limit=${currentPageSize}&page=${page}`;
      
      const res = await axios.get(searchUrl);
      setGames(res.data.games);
      setTotalCount(res.data.total || res.data.count);
      setCurrentPage(page);
      setPageInputValue(page.toString()); // 同步输入框值
      setTotalPages(Math.ceil((res.data.total || res.data.count) / currentPageSize));
      setPageSize(currentPageSize);
    } catch (error) {
      console.error('比赛搜索失败:', error);
      alert('搜索失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 棋手搜索提交
  const handlePlayerSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    // 新搜索时保持当前的每页显示数量设置
    handlePlayerSearch(1);
  };

  // 比赛搜索提交
  const handleTournamentSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    // 新搜索时保持当前的每页显示数量设置
    handleTournamentSearch(1);
  };

  // 监听窗口大小变化，动态调整每页显示数量（仅作为默认值）
  useEffect(() => {
    const handleResize = () => {
      const newPageSize = getPageSize();
      if (newPageSize !== pageSize && !isCustomSizeApplied) {
        // 只有当用户没有应用自定义时，才根据屏幕大小调整
        setCustomPageSize(newPageSize);
        setPageSize(newPageSize);
      }
    };
    
    // 初始设置
    handleResize();
    
    // 添加监听
    window.addEventListener('resize', handleResize);
    
    // 清理
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [pageSize, isCustomSizeApplied]);

  // 翻页
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      if (searchType === 'player') {
        handlePlayerSearch(page);
      } else {
        handleTournamentSearch(page);
      }
    }
  };

  // 刷新当前页（使用当前的每页显示数量和输入框的页码）
  const refreshCurrentPage = () => {
    if (searched) {
      // 使用输入框的值（用户可能输入了但未确认）
      const page = parseInt(pageInputValue);
      const targetPage = (!isNaN(page) && page >= 1 && page <= totalPages) ? page : currentPage;
      
      if (searchType === 'player') {
        handlePlayerSearch(targetPage);
      } else {
        handleTournamentSearch(targetPage);
      }
    }
  };

  // 清空搜索状态
  const clearSearch = () => {
    setPlayer1('');
    setPlayer2('');
    setTournament('');
    setGames([]);
    setSearched(false);
    setTotalCount(0);
    setCurrentPage(1);
    setTotalPages(0);
    setPageInputValue('1');
    setSearchType('player');
    // 清除 localStorage
    localStorage.removeItem('goSearchState');
  };

  // 渲染分页按钮
  const renderPagination = () => {
    const showPageNav = totalPages > 1;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        {/* 左侧：每页显示数量选择器 */}
        <div className="flex items-center gap-1 text-xs">
          <span className="text-gray-600">每页显示:</span>
          <select
            value={customPageSize}
            onChange={(e) => {
              const newSize = parseInt(e.target.value);
              setCustomPageSize(newSize);
              setIsCustomSizeApplied(true);
              setPageSize(newSize);
              
              if (searched) {
                // 立即刷新
                const performSearchWithSize = async (page = 1) => {
                  try {
                    setLoading(true);
                    let searchUrl = '/api/games/search?';
                    const params = [];
                    
                    if (searchType === 'player') {
                      if (player1.trim()) {
                        params.push(`player1=${encodeURIComponent(player1.trim())}`);
                      }
                      if (player2.trim()) {
                        params.push(`player2=${encodeURIComponent(player2.trim())}`);
                      }
                    } else {
                      if (tournament.trim()) {
                        params.push(`tournament=${encodeURIComponent(tournament.trim())}`);
                      }
                    }
                    
                    params.push(`limit=${newSize}`);
                    params.push(`page=${page}`);
                    searchUrl += params.join('&');
                    
                    const res = await axios.get(searchUrl);
                    setGames(res.data.games);
                    setTotalCount(res.data.total || res.data.count);
                    setCurrentPage(page);
                    setPageInputValue(page.toString());
                    setTotalPages(Math.ceil((res.data.total || res.data.count) / newSize));
                  } catch (error) {
                    console.error('搜索失败:', error);
                    alert('搜索失败，请重试');
                  } finally {
                    setLoading(false);
                  }
                };
                
                performSearchWithSize(1);
              }
            }}
            className="px-1 py-0.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            {pageSizeOptions.map(size => (
              <option key={size} value={size}>{size} 条</option>
            ))}
          </select>


        </div>
        
        {/* 中间：分页按钮组 + 操作按钮 */}
        <div className="flex flex-col sm:flex-row items-center gap-2">
          {showPageNav && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-0.5 text-xs rounded border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="跳转到首页"
              >
                首页
              </button>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-2 py-0.5 text-xs rounded border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="上一页"
              >
                上一页
              </button>
              
              {startPage > 1 && (
                <>
                  <button
                    onClick={() => goToPage(1)}
                    className="px-2 py-0.5 text-xs rounded border hover:bg-gray-100"
                  >
                    1
                  </button>
                  {startPage > 2 && <span className="px-1 text-xs">...</span>}
                </>
              )}
              
              {pages.map(page => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`px-2 py-0.5 text-xs rounded border ${
                    page === currentPage
                      ? 'bg-blue-600 text-white'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              ))}
              
              {endPage < totalPages && (
                <>
                  {endPage < totalPages - 1 && <span className="px-1 text-xs">...</span>}
                  <button
                    onClick={() => goToPage(totalPages)}
                    className="px-2 py-0.5 text-xs rounded border hover:bg-gray-100"
                  >
                    {totalPages}
                  </button>
                </>
              )}
              
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-2 py-0.5 text-xs rounded border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="下一页"
              >
                下一页
              </button>
              <button
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-0.5 text-xs rounded border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="跳转到末页"
              >
                末页
              </button>
            </div>
          )}
          
          {/* 页码跳转输入框（带上下箭头）+ 操作按钮 */}
          <div className="flex items-center gap-1 text-xs">
            {showPageNav && (
              <>
                <span className="text-gray-600">跳转到:</span>
                <div className="flex items-center">
                  <button
                    onClick={() => {
                      const newPage = Math.max(1, currentPage - 1);
                      goToPage(newPage);
                    }}
                    disabled={currentPage <= 1}
                    className="px-1.5 py-0.5 border border-gray-300 rounded-l hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="上一页"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={pageInputValue}
                    onChange={(e) => {
                      setPageInputValue(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const page = parseInt(e.target.value);
                        if (page >= 1 && page <= totalPages) {
                          goToPage(page);
                        } else {
                          alert(`请输入1-${totalPages}之间的页码`);
                          setPageInputValue(currentPage.toString());
                        }
                      }
                    }}
                    onBlur={(e) => {
                      const page = parseInt(e.target.value);
                      if (page >= 1 && page <= totalPages) {
                        goToPage(page);
                      } else {
                        setPageInputValue(currentPage.toString());
                      }
                    }}
                    className="w-12 px-1 py-0.5 text-center border-y border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={() => {
                      const newPage = Math.min(totalPages, currentPage + 1);
                      goToPage(newPage);
                    }}
                    disabled={currentPage >= totalPages}
                    className="px-1.5 py-0.5 border border-gray-300 rounded-r hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="下一页"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                <span className="text-gray-600">页</span>
              </>
            )}
            <button
              onClick={refreshCurrentPage}
              title="刷新当前页"
              className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded border border-gray-300 hover:bg-gray-200 transition flex items-center gap-1 ml-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              刷新
            </button>
            <button
              onClick={clearSearch}
              title="清空搜索"
              className="px-2 py-0.5 text-xs bg-red-50 text-red-700 rounded border border-red-300 hover:bg-red-100 transition flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 0 00-1-1h-4a1 0 00-1 1v3M4 7h16" />
              </svg>
              清空
            </button>
          </div>
        </div>
        
        {/* 右侧：当前页信息 */}
        <div className="flex items-center gap-2">
          <div className="text-xs text-gray-600">
            第 {currentPage} 页，共 {totalPages} 页
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-3 min-h-screen flex flex-col">
      {/* 搜索区域 - 紧凑设计 */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="md:space-y-4 space-y-3">
          {/* 桌面端：合并为一栏的搜索布局 */}
          <div className="hidden md:flex items-end gap-3">
            {/* 棋手1输入框 */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">棋手一:</label>
              <input
                type="text"
                value={player1}
                onChange={(e) => setPlayer1(e.target.value)}
                placeholder="姓名或拼音首字母组合"
                className="w-44 px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            {/* 棋手2输入框 */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">棋手二:</label>
              <input
                type="text"
                value={player2}
                onChange={(e) => setPlayer2(e.target.value)}
                placeholder="可选"
                className="w-44 px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            {/* 搜索棋手按钮 */}
            <button
              onClick={handlePlayerSubmit}
              disabled={loading && searchType === 'player'}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loading && searchType === 'player' ? '搜索中...' : '搜索棋手'}
            </button>
            
            {/* 比赛输入框 */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">赛事名:</label>
              <input
                type="text"
                value={tournament}
                onChange={(e) => setTournament(e.target.value)}
                placeholder="赛事名或拼音首字母组合"
                className="w-56 px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            
            {/* 搜索比赛按钮 */}
            <button
              onClick={handleTournamentSubmit}
              disabled={loading && searchType === 'tournament'}
              className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loading && searchType === 'tournament' ? '搜索中...' : '搜索比赛'}
            </button>
          </div>
          
          {/* 移动端/平板端：两行布局 */}
          <div className="md:hidden space-y-3">
            {/* 棋手搜索行 */}
            <div className="grid grid-cols-[auto_auto] gap-1">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">棋手一:</label>
                <input
                  type="text"
                  value={player1}
                  onChange={(e) => setPlayer1(e.target.value)}
                  placeholder="或首字母组合"
                  className="w-[8em] px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">棋手二:</label>
                <input
                  type="text"
                  value={player2}
                  onChange={(e) => setPlayer2(e.target.value)}
                  placeholder="可选"
                  className="flex-1 min-w-[70px] px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            {/* 比赛搜索行 */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">赛事名:</label>
              <input
                type="text"
                value={tournament}
                onChange={(e) => setTournament(e.target.value)}
                placeholder="赛事名或拼音首字母组合"
                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            
            {/* 移动端按钮组 */}
            <div className="flex gap-2 justify-between">
              <button
                onClick={handlePlayerSubmit}
                disabled={loading && searchType === 'player'}
                className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading && searchType === 'player' ? '搜索中...' : '搜索棋手'}
              </button>
              <button
                onClick={handleTournamentSubmit}
                disabled={loading && searchType === 'tournament'}
                className="flex-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading && searchType === 'tournament' ? '搜索中...' : '搜索比赛'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 搜索结果 */}
      {searched && (
        <div className={`bg-white rounded-lg shadow overflow-hidden flex-1 flex flex-col ${games.length === 0 ? 'border-0' : ''}`}>
          {/* 结果统计 */}
          <div className="px-4 py-2 border-b bg-gray-50 flex justify-between items-center text-sm">
            <div>
              <span className="font-medium">共 {totalCount} 局</span>
              <span className="text-gray-400 ml-2">第 {currentPage}/{totalPages} 页</span>
            </div>
            <div className="text-gray-500">
              {searchType === 'tournament' ? (
                <span>赛事名: {getDisplayTournamentName(tournament, games)}</span>
              ) : player1 && player2 ? (
                <span>{getDisplayPlayerName(player1, games, true)} vs {getDisplayPlayerName(player2, games, false)}</span>
              ) : player1 ? (
                <span>{getDisplayPlayerName(player1, games, true)} 的对局</span>
              ) : player2 ? (
                <span>{getDisplayPlayerName(player2, games, false)} 的对局</span>
              ) : null}
            </div>
          </div>

          {/* 棋谱列表 */}
          {games.length > 0 ? (
            <div className="flex-1 overflow-auto">
              {/* 桌面端表格视图 */}
              <div className="hidden md:block">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">#</th>
                      <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">黑方</th>
                      <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">结果</th>
                      <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">白方</th>
                      <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">日期</th>
                      <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">赛事名</th>
                      <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {games.map((game, index) => (
                      <tr key={game.id} className="hover:bg-blue-50">
                        <td className="px-3 py-1 text-gray-400">
                          {(currentPage - 1) * pageSize + index + 1}
                        </td>
                        <td className="px-3 py-1 font-medium">{game.black_player}</td>
                        <td className="px-3 py-1 font-medium text-gray-700">{game.result}</td>
                        <td className="px-3 py-1 font-medium">{game.white_player}</td>
                        <td className="px-3 py-1 text-gray-700 whitespace-nowrap">{game.play_date}</td>
                        <td className="px-3 py-1 text-gray-700 max-w-xs truncate" title={game.tournament}>
                          {game.tournament}
                        </td>
                        <td className="px-3 py-1">
                          <Link
                            to={`/game/${game.id}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline text-xs"
                          >
                            查看
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* 移动端卡片视图 */}
              <div className="md:hidden space-y-2 p-3">
                {games.map((game, index) => (
                  <div key={game.id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-gray-500">#{((currentPage - 1) * pageSize + index + 1)}</span>
                          <span className="text-sm font-medium">{game.black_player}</span>
                          <span className="text-sm font-medium text-gray-700">{game.result}</span>
                          <span className="text-sm font-medium">{game.white_player}</span>
                        </div>
                        <div className="text-sm text-gray-700">
                          <div className="mb-1">{game.play_date}</div>
                          <div className="truncate" title={game.tournament}>{game.tournament}</div>
                        </div>
                      </div>
                      <Link
                        to={`/game/${game.id}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline text-xs whitespace-nowrap ml-2"
                      >
                        查看棋谱
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex-1"></div>
          )}
          
          {/* 分页 */}
          {games.length > 0 && (
            <div className="px-4 py-2 border-t bg-gray-50">
              {renderPagination()}
            </div>
          )}
        </div>
      )}

      {/* 未搜索时的提示 */}
      {!searched && (
        <div className="bg-white rounded-lg shadow flex-1 flex items-center justify-center">
          <div className="text-center py-12">
            <div className="text-5xl mb-3">⚫⚪</div>
            <div className="text-lg text-gray-600 mb-1">围棋棋谱检索网</div>
          </div>
        </div>
      )}
    </div>
  );
}
